import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { AuthenticationService } from '../services/authentication.service';
import { UserModel } from '../models/user.model';
import { RouteService } from '../../routing/services/route.service';

@Injectable()
export class AuthGuard implements CanActivate {

    /**
     * @param {AuthenticationService} auth
     * @param {RouteService} routeService
     */
    constructor(private auth: AuthenticationService, private routeService: RouteService) {
    }

    /**
     * The AuthGuard checks if the user may access the requested page.
     * We'll check if the user is logged in and has the right permissions.
     *
     * @param {ActivatedRouteSnapshot} route
     * @param {RouterStateSnapshot} state
     * @returns {Promise<boolean> | Promise}
     */
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return new Promise((resolve) => {

            // Check if the user is authenticated
            if (!this.auth.check()) {
                // console.log('Not authorized, forward to login page');

                // Deny access and forward to login
                this.forwardToLoginPage();

                resolve(false);

                return;
            }

            this.auth.user().subscribe((user: UserModel) => {
                // Check if the user has the needed permissions
                if (this.userHasPermissions(user, route)) {
                    // console.log('Authorized and has permission, go to page');
                    // Access granted
                    resolve(true);
                } else {
                    // console.log('Authorized but does not have permission, forward to error page');
                    // Access denied

                    resolve(false);
                    this.forwardToErrorPage();
                }
            }, () => {
                // console.log('Error, forward to login page');

                // An error occurred, deny access and forward to login

                resolve(false);
                this.forwardToLoginPage();
            });
        });
    }

    /**
     * Indicates if current authenticated user has permissions
     * to view the given route.
     *
     * @param user
     * @param route
     * @return {boolean}
     */
    private userHasPermissions(user, route) {
        const routeData = route.routeConfig.data;

        // Check if the user needs permissions to view the route
        if (routeData && routeData['permission']) {
            const permission = routeData['permission'];

            // Check if the user has the needed permissions
            if (user.can('entities.' + permission) || user.can(permission)) {
                return true;
            }
        } else {
            return true;
        }

        return false;
    }

    /**
     * Navigate the user to the login page.
     *
     * @returns {Promise<boolean>}
     */
    private forwardToLoginPage(): Promise<boolean> {
        return this.routeService.navigate('login');
    }

    /**
     * Navigate the user to an error page.
     *
     * @returns {Promise<boolean>}
     */
    private forwardToErrorPage(): Promise<boolean> {
        return this.routeService.navigate('403');
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/authentication/guards/auth.guard.ts